/* eslint-disable new-cap */

import Backbone from 'backbone';
import _ from 'underscore';
import $ from 'jquery';
import Promise from 'bluebird';
import { SimpleForm } from '@bingads-webui/simple-form';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { ErrorMessageView } from './error-message-view';
import { getImportOptionFields as getDefaultImportOptionFields } from './fields-config/index';
import {
  parseEditScheduledImportOptions,
  parseExistingImportOptions,
  parseJsonFormatImportOptions,
  parseModelToImportUserPreference,
  pickModelPropertiesByFields,
} from './parsers/index';
import { FlattenedSchemaGenerator } from './flattened-schemas';

import helpButtonTemplate from './help-button-template.pug';
import importOptionsFormTemplate from './import-options-form-template.pug';

const UETTagStatus = {
  Active: 1,
  Unverified: 2,
  Inactive: 3,
};

export class ImportOptionsForm extends Backbone.View {
  initialize({
    deps,
    logTrace,
    previousImportOptions, // options from api call of getGoogleImportUserPreferences
    editTaskItemOptions, // deprecate with pilot isGetUserPreferenceJsonFormatEnabled // TODO: remove
    jsonFormatEditTaskItemOptions, // create/edit options of google wizard
    jsonFormatImportOptions, // used for schedule page log grid
    includePausedCampaigns,
    toggleContinueButton,
    getImportOptionFields,
    otherFieldsExpanded = false,
    isPauseAdGroupIfNoLocationSupportedEnabled = false,
    isAudienceTargetForFBImportOptionEnabled = false,
    isGoogleImport = false,
    shouldShowCurrencyConvertor = false,
    accountCurrencyMap = null,
    importAccountCurrencyTitle,
  }) {
    this.deps = deps;
    this.logTrace = logTrace;
    this.flattenedSchemaGenerator = new FlattenedSchemaGenerator({ deps });
    this.i18n = deps.i18n;
    this.subFieldsBlacklist = deps.subFieldsBlacklist;
    this.toggleContinueButton = toggleContinueButton;
    this.includePausedCampaigns = includePausedCampaigns;
    this.getImportOptionFields = getImportOptionFields || getDefaultImportOptionFields;
    // This covers single-account and multi-account Google Import in New UI, Old UI, and MAE
    this.shouldEnableBSCSmartImport = ImportOptionsForm.isBSCSmartImportEnabled(deps) &&
      isGoogleImport;
    this.isAudienceTargetForFBImportOptionEnabled = isAudienceTargetForFBImportOptionEnabled;
    this.isGoogleLogoExtensionImportOptionEnabled = ImportOptionsForm.isGoogleLogoExtensionImportOptionEnabled(deps) && isGoogleImport;
    this.isGoogleUpdateAdUrlsImportOptionEnabled = ImportOptionsForm.isGoogleUpdateAdUrlsImportOptionEnabled(deps);
    this.isGoogleUpdateSitelinkUrlsImportOptionEnabled = ImportOptionsForm.isGoogleUpdateSitelinkUrlsImportOptionEnabled(deps);
    this.isGoogleAccountNegativeKeywordsImportOptionEnabled = ImportOptionsForm.isGoogleAccountNegativeKeywordsImportOptionEnabled(deps) && isGoogleImport;
    this.isGoogleAdCustomizerAttributesImportOptionEnabled = ImportOptionsForm.isGoogleAdCustomizerAttributesImportOptionEnabled(deps) && isGoogleImport;
    this.isGoogleConversionGoalsImportOptionEnabled = ImportOptionsForm.isGoogleConversionGoalsImportOptionEnabled(deps) && isGoogleImport;
    this.isBrandSuitabilityImportOptionEnabled = ImportOptionsForm.isBrandSuitabilityImportOptionEnabled(deps) && isGoogleImport;
    const pilotAndStoreList = {
      isMultiAccountImport: deps.isMultiAccountImport,
      isPageFeedEnabled: ImportOptionsForm.isPageFeedEnabled(deps),
      storeList: deps.storeList,
      isDeviceTargetOptimizationEnabled:
        ImportOptionsForm.isDeviceTargetOptimizationEnabled(deps),
      isAdScheduleUseSearcherTimezoneEnabled:
        ImportOptionsForm.isAdScheduleUseSearcherTimezoneEnabled(deps),
      isPauseAIMAdGroupIfAllAudienceCriterionNotImportedImportOptionEnabled:
        ImportOptionsForm
          .isPauseAIMAdGroupIfAllAudienceCriterionNotImportedImportOptionEnabled(deps),
      isGoogleImageExtensionImportOptionEnabled:
        ImportOptionsForm
          .isGoogleImageExtensionImportOptionEnabled(deps),
      isAudienceTargetForFBImportOptionEnabled,
      isGoogleLogoExtensionImportOptionEnabled: this.isGoogleLogoExtensionImportOptionEnabled,
      isGoogleUpdateAdUrlsImportOptionEnabled: this.isGoogleUpdateAdUrlsImportOptionEnabled,
      isGoogleUpdateSitelinkUrlsImportOptionEnabled: this.isGoogleUpdateSitelinkUrlsImportOptionEnabled,
      isGoogleAccountNegativeKeywordsImportOptionEnabled: this.isGoogleAccountNegativeKeywordsImportOptionEnabled,
      isGoogleAdCustomizerAttributesImportOptionEnabled: this.isGoogleAdCustomizerAttributesImportOptionEnabled,
      isGoogleConversionGoalsImportOptionEnabled: this.isGoogleConversionGoalsImportOptionEnabled,
      isBrandSuitabilityImportOptionEnabled: this.isBrandSuitabilityImportOptionEnabled,
    };
    let completeModel;

    if (jsonFormatImportOptions) {
      completeModel = parseJsonFormatImportOptions(_.extend(
        {},
        {
          options: _.extend(
            {
              UserPreferenceTrackingTemplate: jsonFormatImportOptions.AdWordsAccountTrackingTemplate
              || jsonFormatImportOptions.AdWordsAccountFinalUrlSuffix,
            },
            jsonFormatImportOptions
          ),
        },
        pilotAndStoreList
      ));
    } else {
      completeModel = parseJsonFormatImportOptions(_.extend(
        {},
        { options: previousImportOptions },
        pilotAndStoreList
      ));

      if (includePausedCampaigns) {
        completeModel.ImportNewOption_ImportNewActiveEntitiesOnly = true;
      }

      const urlOptions = {
        TrackingTemplateValue: completeModel.TrackingTemplateValue,
        FinalUrlSuffixValue: completeModel.FinalUrlSuffixValue,
      };
      // eslint-disable-next-line max-len
      if (jsonFormatEditTaskItemOptions) {
        completeModel = _.extend(
          {},
          completeModel,
          parseJsonFormatImportOptions(_.extend(
            {},
            { options: jsonFormatEditTaskItemOptions },
            pilotAndStoreList
          )),
          urlOptions
        );
      } else if (editTaskItemOptions) {
        completeModel = _.extend(
          {},
          completeModel,
          parseEditScheduledImportOptions({
            options: editTaskItemOptions,
            isMultiAccountImport: deps.isMultiAccountImport,
            isPageFeedEnabled: ImportOptionsForm.isPageFeedEnabled(deps),
            isDeviceTargetOptimizationEnabled:
              ImportOptionsForm.isDeviceTargetOptimizationEnabled(deps),
            isAdScheduleUseSearcherTimezoneEnabled:
              ImportOptionsForm.isAdScheduleUseSearcherTimezoneEnabled(deps),
            isPauseAIMAdGroupIfAllAudienceCriterionNotImportedImportOptionEnabled:
              ImportOptionsForm
                .isPauseAIMAdGroupIfAllAudienceCriterionNotImportedImportOptionEnabled(deps),
            isGoogleImageExtensionImportOptionEnabled:
              ImportOptionsForm
                .isGoogleImageExtensionImportOptionEnabled(deps),
            isAudienceTargetForFBImportOptionEnabled,
            isGoogleLogoExtensionImportOptionEnabled: this.isGoogleLogoExtensionImportOptionEnabled,
            isGoogleUpdateAdUrlsImportOptionEnabled: this.isGoogleUpdateAdUrlsImportOptionEnabled,
            isGoogleUpdateSitelinkUrlsImportOptionEnabled: this.isGoogleUpdateSitelinkUrlsImportOptionEnabled,
            isGoogleAccountNegativeKeywordsImportOptionEnabled: this.isGoogleAccountNegativeKeywordsImportOptionEnabled,
            isGoogleAdCustomizerAttributesImportOptionEnabled: this.isGoogleAdCustomizerAttributesImportOptionEnabled,
            isGoogleConversionGoalsImportOptionEnabled: this.isGoogleConversionGoalsImportOptionEnabled,
            isBrandSuitabilityImportOptionEnabled: this.isBrandSuitabilityImportOptionEnabled,
          }),
          urlOptions
        );
        // eslint-disable-next-line max-len
      }
    }

    // isCustomerLevel = true if it is customer level's settings
    if (deps.isCustomerLevel) {
      completeModel.isCustomerLevel = true;
    }
    // hide the detail view if it is customer level's setting but log grid.
    if (deps.isHideUrlDetailView) {
      completeModel.isHideUrlDetailView = true;
    }
    // show currency converter for single account smart import
    // TODO: show currency converter for multi account smart import
    if (shouldShowCurrencyConvertor) {
      completeModel.AccountCurrencyMap = accountCurrencyMap;
      if (_.isUndefined(completeModel.EnableAutoCurrencyConversion) ||
        _.isNull(completeModel.EnableAutoCurrencyConversion)) {
        completeModel.EnableAutoCurrencyConversion = true;
      }
      if (this.logTrace) {
        this.logTrace('Show currency convertor in import options step');
      }
    }

    const fields = this.getImportOptionFields({
      i18n: this.i18n,
      logTrace: this.logTrace,
      model: completeModel,
      subFieldsBlacklist: this.subFieldsBlacklist,
      flattenedSchemaGenerator: this.flattenedSchemaGenerator,
      config: this.deps.config,
      UetTagSelectorView: this.deps.UetTagSelectorView,
      minAdjust: deps.schema.ImportAdjustmentForNumericProperty.properties.AdjustmentValue.minimum,
      maxAdjust: deps.schema.ImportAdjustmentForNumericProperty.properties.AdjustmentValue.maximum,
      isMultiAccountImport: deps.isMultiAccountImport,
      isForVNext: deps.isForVNext,
      otherFieldsExpanded: otherFieldsExpanded || isAudienceTargetForFBImportOptionEnabled,
      shouldEnableBSCSmartImport: this.shouldEnableBSCSmartImport,
      shouldShowCurrencyConvertor,
      importAccountCurrencyTitle,
      isGoogleLogoExtensionImportOptionEnabled: this.isGoogleLogoExtensionImportOptionEnabled,
      isGoogleUpdateAdUrlsImportOptionEnabled: this.isGoogleUpdateAdUrlsImportOptionEnabled,
      isGoogleUpdateSitelinkUrlsImportOptionEnabled: this.isGoogleUpdateSitelinkUrlsImportOptionEnabled,
      isGoogleAccountNegativeKeywordsImportOptionEnabled: this.isGoogleAccountNegativeKeywordsImportOptionEnabled,
      isGoogleAdCustomizerAttributesImportOptionEnabled: this.isGoogleAdCustomizerAttributesImportOptionEnabled,
      isGoogleConversionGoalsImportOptionEnabled: this.isGoogleConversionGoalsImportOptionEnabled,
      isBrandSuitabilityImportOptionEnabled: this.isBrandSuitabilityImportOptionEnabled,
    });

    const fieldsToPick = _.flatten(_.map(fields, field => field.fields));

    if (!_.contains(this.subFieldsBlacklist, 'ImportAccountUrlOptions')) {
      fieldsToPick.push({
        property: 'TrackingTemplateValue',
      });
      fieldsToPick.push({
        property: 'FinalUrlSuffixValue',
      });
    }

    if (isAudienceTargetForFBImportOptionEnabled) {
      fieldsToPick.push({
        property: 'ImportNewAudienceTargets',
      });
      fieldsToPick.push({
        property: 'UpdateAudienceTargets',
      });
    }

    const model = pickModelPropertiesByFields(completeModel, fieldsToPick);
    const properties = [
      'ImportAccountUrlOptions',
      'TrackingTemplateValue',
      'ImportNewEntities',
      'UpdateEntityPreferences',
      'ImportDeletedEntities',
      'DefaultBidsBelowMinToAdCenterMinBid',
      'DefaultCampaignBudgetBelowMinToAdCenterMinBudget',
      'ImportAppendDestUrlPreference',
      'ImportSearchAndReplaceDestUrlPreference',
      'AppendPreferencesForTrackingTemplate',
      'SearchAndReplacePreferencesForTrackingTemplate',
      'ImportUpdateEntityPreference_UpdateNameForExistingCampaigns',
      'ImportNewOption_ImportNewCampaignsAndMarkAsPaused',
      'ImportNewOption_ImportNewActiveEntitiesOnly',
      'StoreIdForImportedProductAdExtensionsFlag',
      'StoreIdForImportedProductAdExtensions',
      'ImportPrefixAndSuffixForStringProperty',
      'ImportSearchAndReplaceForStringProperty',
      'SearchAndReplaceForCustomParameters',
      'SearchAndReplaceForFinalURLSuffix',
    ];

    properties.push('FinalUrlSuffixValue');
    properties.push('AdjustmentForCampaignBudgets', 'AdjustmentForBids', 'PauseParentIfNoLocationSupported', 'EnableParentLocationMapping');

    properties.push('RaiseLowBidsToMinServingBidsForBsc');
    properties.push('AutoDeviceBidOptimization');
    properties.push('AdScheduleUseSearcherTimezone');
    properties.push('ImportSearchAndDsaMixedCampaignAsSearchCampaign');
    properties.push('ImportNewOption_ImportAllAdUnderAnAdGroup');
    properties.push('UpdateLabelsAndAssociations', 'ImportNewLabelsAndAssociations');
    properties.push('ImportNewCustomFiltersForShoppingCampaigns', 'UpdateCustomFiltersForShoppingCampaigns');
    properties.push('ImportUpdateEntityPreference_UpdateCampaignBudget', 'ImportUpdateEntityPreference_UpdateAdGroupAndKeywordBids');
    properties.push('ImportUpdateEntityPreference_UpdateAdGroupAndKeywordBidStrategies');

    properties.push('ImportUET');
    properties.push('AssociatedUETTagId');
    properties.push('PauseAIMAdGroupIfAllAudienceCriterionNotImported');
    properties.push('ImportNewOption_ImportNewImageAdExtensions', 'ImportUpdateEntityPreference_UpdateImageAdExtensions');

    if (isPauseAdGroupIfNoLocationSupportedEnabled) {
      properties.push('PauseAdGroupIfNoLocationSupported');
    }

    if (isAudienceTargetForFBImportOptionEnabled) {
      properties.push('NoAudienceTargetForFBImportOption');
    }

    properties.push('RenameCampaignNameWithSuffix');

    if (shouldShowCurrencyConvertor) {
      properties.push('EnableAutoCurrencyConversion');
    }

    if (this.isGoogleLogoExtensionImportOptionEnabled) {
      properties.push('ImportNewOption_ImportNewLogoAdExtensions', 'ImportUpdateEntityPreference_UpdateLogoAdExtensions');
    }

    if (this.isGoogleUpdateAdUrlsImportOptionEnabled) {
      properties.push('ImportUpdateEntityPreference_UpdateAdUrls');
    }

    if (this.isGoogleUpdateSitelinkUrlsImportOptionEnabled) {
      properties.push('ImportUpdateEntityPreference_UpdateSitelinkUrls');
    }

    if (this.isGoogleAccountNegativeKeywordsImportOptionEnabled) {
      properties.push('ImportNewOption_ImportNewAccountNegativeKeywords');
    }

    if (this.isGoogleAdCustomizerAttributesImportOptionEnabled) {
      properties.push('ImportUpdateEntityPreference_UpdateAdCustomizerAttributes');
    }

    if (this.isGoogleConversionGoalsImportOptionEnabled) {
      properties.push('ImportNewOption_ImportNewConversionGoals', 'ImportUpdateEntityPreference_UpdateConversionGoals');
    }

    if (this.isBrandSuitabilityImportOptionEnabled) {
      properties.push('ImportNewOption_ImportNewBrandSuitability', 'ImportUpdateEntityPreference_UpdateBrandSuitability');
    }

    const schema = this.flattenedSchemaGenerator.getFlattenedSchemaWithProperties(...properties);

    this.importOptionsForm = new SimpleForm({
      schema,
      fields,
      model,
      showButtons: false,
      i18n: this.deps.i18nAmdLoader,
    });

    this.errorMessageView = new ErrorMessageView({
      errorMessage: deps.i18n.getString('Validation_ImportOptionRequired'),
    });
    this.uetErrorMessageView = new ErrorMessageView({
      errorMessage: deps.i18n.getString('Import_ImportOptions_UncheckUETWarning'),
    });
  }

  events() {
    return {
      'input input, select, textarea': 'enableContinueButton',
      'click [data-property="ImportNewEntities"] .campaigns-checkbox': 'checkSubitems',
      'click [data-property="ImportNewEntities"] .advanced-options .checkbox': 'toggleNewAllCheckboxState',
      'click [data-property="UpdateEntityPreferences"] .campaigns-checkbox': 'checkUpdateSubitems',
      'click .form-label-container .control-label': 'blockLabelClick',
      'click [data-property="ImportNewOption_ImportNewAudienceTargets"]': 'toggleAudienceListsTargets',
      'click [data-property="NoAudienceTargetForFBImportOption"]': 'toggleAudienceListsTargets',
      'click [data-property="ImportUET"] .checkbox': 'toggleUET',
    };
  }

  blockLabelClick(event) {
    event.preventDefault();
  }

  checkSubitems({ isFirstUpdate = false } = {}) {
    // handle click on indeterminate scenario; directly modifying attribute
    // doesn't work b/c it's bound to a ko-observable in this case
    if (this.isLastNewStateIndeterminate) {
      this.importOptionsForm.viewModel.ImportNewEntities(true);
      this.isLastNewStateIndeterminate = false;
    }

    if (this.importOptionsForm.viewModel.ImportNewEntities()) {
      this.importOptionsForm.viewControls.ImportNewEntities.enableAdvancedOptions();
      if (!isFirstUpdate) {
        // don't select all sub items on first load; use fetched import preferences
        this.importOptionsForm.viewControls.ImportNewEntities.selectAllOptions();
      }
    } else {
      this.importOptionsForm.viewControls.ImportNewEntities.disableAdvancedOptions();
    }

    this.toggleAudienceListsTargets();
  }

  checkUpdateSubitems({ isFirstUpdate = false } = {}) {
    // expand advanced options whenever the user checks/unchecks the box
    if (!isFirstUpdate) {
      this.importOptionsForm.viewControls.UpdateEntityPreferences
        .toggleAdvancedOptionsExpansion(true);
    }

    if (this.importOptionsForm.viewModel.UpdateEntityPreferences()) {
      this.importOptionsForm.viewControls.UpdateEntityPreferences.enableAdvancedOptions();

      this.importOptionsForm.viewControls.UpdateEntityPreferences.toggleWarningMessage(false);
    } else {
      this.importOptionsForm.viewControls.UpdateEntityPreferences.disableAdvancedOptions();

      if (this.areLinkedUpdateItemsChecked()) {
        this.uncheckLinkedUpdateItems();
        this.importOptionsForm.viewControls.UpdateEntityPreferences.toggleWarningMessage(true);
        this.clickCollapseGroupLink(1);
      }
    }
  }

  areLinkedUpdateItemsChecked() {
    const {
      ImportUpdateEntityPreference_UpdateCampaignBudget: prefUpdateCampaignBudget,
      ImportUpdateEntityPreference_UpdateAdGroupAndKeywordBids: prefUpdateAdGroupAndKeywordBids,
      ImportUpdateEntityPreference_UpdateAdGroupAndKeywordBidStrategies:
        prefUpdateAdGroupAndKeywordBidStrategies,
    } = this.importOptionsForm.viewModel;

    return (prefUpdateCampaignBudget && prefUpdateCampaignBudget())
      || (prefUpdateAdGroupAndKeywordBids && prefUpdateAdGroupAndKeywordBids())
      || (prefUpdateAdGroupAndKeywordBidStrategies && prefUpdateAdGroupAndKeywordBidStrategies());
  }

  // uncheck two options that have moved to bids and budget section
  uncheckLinkedUpdateItems() {
    const {
      ImportUpdateEntityPreference_UpdateCampaignBudget: prefUpdateCampaignBudget,
      ImportUpdateEntityPreference_UpdateAdGroupAndKeywordBids: prefUpdateAdGroupAndKeywordBids,
      ImportUpdateEntityPreference_UpdateAdGroupAndKeywordBidStrategies:
        prefUpdateAdGroupAndKeywordBidStrategies,
    } = this.importOptionsForm.viewModel;
    if (prefUpdateCampaignBudget) {
      prefUpdateCampaignBudget(false);
    }
    if (prefUpdateAdGroupAndKeywordBids) {
      prefUpdateAdGroupAndKeywordBids(false);
    }
    if (prefUpdateAdGroupAndKeywordBidStrategies) {
      prefUpdateAdGroupAndKeywordBidStrategies(false);
    }
  }

  isUETInLogGrid() {
    return this.importOptionsForm.viewControls.AssociatedUETTagId.disabled;
  }

  toggleAudienceListsTargets(updateUETCheckbox = true) {
    if (this.isUETEnabled() && !this.isUETInLogGrid()) {
      const uetCheckbox = '[data-property="ImportUET"] input';
      const uetTag = '[data-property="AssociatedUETTagId"]';
      const uetTagInput = `${uetTag} select, ${uetTag} input, ${uetTag} textarea`;
      if (this.isAudienceTargetForFBImportOptionEnabled &&
      this.importOptionsForm.viewControls.ImportNewEntities.advancedOptionsView[1] &&
      this.importOptionsForm.viewControls.ImportNewEntities.advancedOptionsView[1]
        .form.viewModel.ImportNewOption_ImportNewAudienceTargets
      ) {
        if (this.importOptionsForm.viewModel.NoAudienceTargetForFBImportOption() === true ||
          this.importOptionsForm.viewModel.NoAudienceTargetForFBImportOption() === false
        ) {
          // eslint-disable-next-line max-len
          this.importOptionsForm.viewControls.ImportNewEntities.advancedOptionsView[1].form.viewModel.ImportNewOption_ImportNewAudienceTargets(!this.importOptionsForm.viewModel.NoAudienceTargetForFBImportOption());
        }
      }

      if (this.importOptionsForm.viewModel.ImportNewEntities() &&
        this.importOptionsForm.viewControls.ImportNewEntities
          .getAdvancedOptionsModel().ImportNewOption_ImportNewAudienceTargets) {
        this.$(uetCheckbox).prop('disabled', false);
        this.$(uetTagInput).prop('disabled', false);
        if (updateUETCheckbox) {
          this.importOptionsForm.viewModel.ImportUET(true);
        }
      } else {
        this.$(uetCheckbox).prop('disabled', true);
        this.$(uetTagInput).prop('disabled', true);
        if (updateUETCheckbox) {
          this.importOptionsForm.viewModel.ImportUET(false);
        }
      }
      this.uetErrorMessageView.setVisibility(false);
    }
  }

  toggleNewAllCheckboxState() {
    const allCheckbox = this.$('[data-property="ImportNewEntities"] .campaigns-checkbox');

    const importNewEntities = this.importOptionsForm.viewControls.ImportNewEntities;
    const areAllOptionsDisabled = importNewEntities.areAllOptionsDisabled();
    const areSomeOptionsSelected = importNewEntities.areSomeOptionsSelected();
    const areAllOptionsSelected = importNewEntities.areAllOptionsSelected();
    if (importNewEntities.hideAdvancedOptions && areSomeOptionsSelected && !areAllOptionsDisabled) {
      // if hide advanced options, don't show indeterminate state
      allCheckbox.prop('indeterminate', false);
      this.importOptionsForm.viewModel.ImportNewEntities(true);
      this.isLastNewStateIndeterminate = false;
    } else if (areAllOptionsSelected && !areAllOptionsDisabled) {
      // if advanced options are all disabled, we will not set ImportNewEntities as true
      allCheckbox.prop('indeterminate', false);
      this.importOptionsForm.viewModel.ImportNewEntities(true);
      this.isLastNewStateIndeterminate = false;
    } else if (areSomeOptionsSelected && !areAllOptionsDisabled) {
      // if advanced options are all disabled, we will not set ImportNewEntities as true
      allCheckbox.prop('indeterminate', true);
      this.importOptionsForm.viewModel.ImportNewEntities(true);
      this.isLastNewStateIndeterminate = true;
    } else {
      allCheckbox.prop('indeterminate', false);
      this.importOptionsForm.viewModel.ImportNewEntities(false);
      this.isLastNewStateIndeterminate = false;
    }
    this.toggleAudienceListsTargets();
  }

  getImportUserPreferences() {
    const model = _.extend(
      {},
      this.importOptionsForm.currentModel(),
      this.importOptionsForm.viewControls.ImportNewEntities.getAdvancedOptionsModel(),
      this.importOptionsForm.viewControls.UpdateEntityPreferences.getAdvancedOptionsModel(),
      {
        AdjustmentForBids_AdjustmentDecrease: this.importOptionsForm.viewControls.AdjustmentForBids_AdjustmentValue.isDecreaseSelected(),
        AdjustmentForCampaignBudgets_AdjustmentDecrease: this.importOptionsForm.viewControls.AdjustmentForCampaignBudgets_AdjustmentValue.isDecreaseSelected(),
      }
    );

    const importUserPreferences = parseModelToImportUserPreference(
      model,
      ImportOptionsForm.isPageFeedEnabled(this.deps),
      !!this.deps.isMultiAccountImport,
      ImportOptionsForm.isDeviceTargetOptimizationEnabled(this.deps),
      ImportOptionsForm.isAdScheduleUseSearcherTimezoneEnabled(this.deps),
      ImportOptionsForm
        .isPauseAIMAdGroupIfAllAudienceCriterionNotImportedImportOptionEnabled(this.deps),
      ImportOptionsForm
        .isGoogleImageExtensionImportOptionEnabled(this.deps),
      this.isAudienceTargetForFBImportOptionEnabled,
      this.isGoogleLogoExtensionImportOptionEnabled,
      this.isGoogleUpdateAdUrlsImportOptionEnabled,
      this.isGoogleUpdateSitelinkUrlsImportOptionEnabled,
      this.isGoogleAccountNegativeKeywordsImportOptionEnabled,
      this.isGoogleAdCustomizerAttributesImportOptionEnabled,
      this.isGoogleConversionGoalsImportOptionEnabled,
      this.isBrandSuitabilityImportOptionEnabled
    );
    return importUserPreferences;
  }

  // name of the observable which stores collapse state is auto-generated and
  // not guaranteed to be the same each time (name is name of all properties in
  // this collapse group concated with a seemingly random number)
  // use jquery selector instead, since this collapse group is guaranteed to be
  // in the same place in the form each time, and there's only one collapse group
  // which has validators which need to potentially be expanded
  getSelectorForCollapseGroup(positionIndex) {
    let nthChild = 0;

    switch (positionIndex) {
      case 0:
        nthChild = 2;
        break;
      case 1:
        nthChild = 4;
        break;
      case 2:
        nthChild = 6;
        break;
      default:
        break;
    }

    return `form div:nth-child(${nthChild})`;
  }

  getSelectorForCollapseGroupLink(positionIndex) {
    return `${this.getSelectorForCollapseGroup(positionIndex)} [data-toggle="collapse"]`;
  }

  getSelectorForCollapseGroupLabel(positionIndex) {
    return `${this.getSelectorForCollapseGroup(positionIndex)}.scheduling-form`;
  }

  clickCollapseGroupLink(positionIndex) {
    const collapseLink = this.$(this.getSelectorForCollapseGroupLink(positionIndex));
    const isGroupCollapsed = collapseLink.hasClass('collapsed');

    if (isGroupCollapsed) {
      collapseLink.click();
    }
  }

  validate() {
    const isValid = this.importOptionsForm.validate() && this.isAnyOptionSelected() &&
      this.checkUETValidate(true);

    if (!isValid) {
      this.clickCollapseGroupLink(2);
    }

    if (this.logTrace && this.isUETEnabled()) {
      this.logTrace(`UET tag checkbox: ${this.importOptionsForm.viewModel.ImportUET() ? 'checked' : 'unchecked'}, tag selector value: ${$('#tagsDropDown').val()}`);
    }
    return isValid;
  }

  isUETEnabled() {
    // to exclude file import
    return this.importOptionsForm.viewModel.ImportUET;
  }

  checkUETValidate(isForContinue) {
    let isValid = true;
    if (this.isUETEnabled()) {
      if (this.importOptionsForm.viewModel.ImportNewEntities() &&
        this.importOptionsForm.viewControls.ImportNewEntities
          .getAdvancedOptionsModel().ImportNewOption_ImportNewAudienceTargets &&
        this.importOptionsForm.viewModel.ImportUET()
      ) {
        isValid = this.importOptionsForm.viewControls.AssociatedUETTagId.validate(isForContinue);
      } else {
        isValid = !this.importOptionsForm.viewModel.ImportUET();
      }
    }

    return isValid;
  }

  uetSortFunc(tag) {
    if (tag && tag.IsPreferredTag) {
      // IsPreferredTag is highest priority.
      // only active status tag can have this bool set to true.
      return 0;
    }

    return UETTagStatus[tag && tag.TrackingStatus];
  }

  createUETTag() {
    if (this.isUETEnabled() &&
      this.importOptionsForm.viewModel.ImportUET() && this.uetTag && !this.uetTag.Id) {
      // create uet
      return new Promise((resolve, reject) => {
        this.importOptionsForm.viewControls.AssociatedUETTagId.save()
          .done((response) => {
            if (_.has(response, 'Id')) {
              // after save succeeds, update view to select the new created tag
              return this.importOptionsForm.viewControls.AssociatedUETTagId
                .updateTags(
                  response.Id,
                  this.uetSortFunc,
                  !this.isUETInLogGrid()
                ).then(() => resolve({
                  newUETCreated: true,
                  hasError: false,
                })).catch(() => reject(response));
            }
            return reject(response);
          })
          .fail(response => reject(response));
      }).finally(withUiBlock());
    }
    return Promise.resolve({
      newUETCreated: false,
      hasError: false,
    });
  }

  isAnyOptionSelected() {
    const preferences = this.getImportUserPreferences();

    const isAnySelected = !!(preferences.ImportNewEntities || preferences.ImportUpdatedEntities ||
        preferences.ImportDeletedEntities ||
        preferences.AdWordsAccountTrackingTemplate || preferences.AdWordsAccountFinalUrlSuffix);

    this.errorMessageView.setVisibility(!isAnySelected);
    return isAnySelected;
  }

  enableContinueButton() {
    if (this.toggleContinueButton) {
      this.toggleContinueButton(true);
    }
  }

  render() {
    const storeIdSelector = '[data-property="StoreIdForImportedProductAdExtensions"]';

    this.$el.html(importOptionsFormTemplate());

    this.$('.advanced-import-options-form').html(this.importOptionsForm.render().$el);

    this.$(this.getSelectorForCollapseGroupLabel(0)).append(this.errorMessageView.render().$el);
    this.$('.form-input-container[data-property="AssociatedUETTagId"]').parent().append(this.uetErrorMessageView.render().$el);

    if (this.importOptionsForm.viewModel.StoreIdForImportedProductAdExtensionsFlag) {
      this.importOptionsForm.viewModel.StoreIdForImportedProductAdExtensionsFlag
        .subscribe(_.partial(this.toggleListDisableState, storeIdSelector).bind(this));
    }

    if (this.importOptionsForm.viewModel.StoreIdForImportedProductAdExtensionsFlag) {
      this.toggleListDisableState(storeIdSelector, this.importOptionsForm.viewModel.StoreIdForImportedProductAdExtensionsFlag()); //eslint-disable-line
    }

    this.checkSubitems({ isFirstUpdate: true });
    this.checkUpdateSubitems({ isFirstUpdate: true });

    this.hackyWayToAddHelpButtons();

    this.toggleNewAllCheckboxState();

    if (this.importOptionsForm.viewControls.AssociatedUETTagId) {
      this.importOptionsForm.viewControls.AssociatedUETTagId.on('update', (tag) => {
        this.importOptionsForm.viewModel.AssociatedUETTagId(tag && tag.Id);
        this.uetTag = tag;
        this.toggleAudienceListsTargets(false);
        // do another validate to correct the wrong UI state for edit step scenario
        this.importOptionsForm.viewControls.AssociatedUETTagId.validate();
      });

      this.importOptionsForm.viewControls.AssociatedUETTagId
        .updateTags(
          this.importOptionsForm.viewModel.AssociatedUETTagId(),
          this.uetSortFunc,
          !this.isUETInLogGrid()
        );
    }

    return this;
  }

  toggleUET(e) {
    // do nothing if the checkbox is diabled
    if ($(e.target).children('input').prop('disabled')) {
      return;
    }
    const uetTag = '[data-property="AssociatedUETTagId"]';
    const uetTagInput = `${uetTag} select, ${uetTag} input, ${uetTag} textarea`;
    this.$(uetTagInput).prop('disabled', !this.importOptionsForm.viewModel.ImportUET());
    if (this.logTrace) {
      this.logTrace('click UET tag checkbox');
    }
  }

  toggleListDisableState(selector, newState) {
    if (newState) {
      this.$(`${selector} select`).removeAttr('disabled');
    } else {
      this.$(`${selector} select`).attr('disabled', 'disabled');
    }
  }

  // Temporarily add help buttons to labels section until support in simple-form is complete
  // Work item: 982346
  // Also, still awaiting final help text and ids from UX
  hackyWayToAddHelpButtons() {
    const { importOptionFieldHelpIds = {} } = this.deps || {};
    const {
      DefaultBidsBelowMinToAdCenterMinBid: helpIdForDefaultBidsBelowMinToAdCenterMinBid = ImportOptionsForm.isImportAutoMinimumBidEnabled(this.deps) ? 'pop_BA_Import_Google_SetUnknownBid' : 'pop_BA_Import_IncreaseToRecommended',
      DefaultCampaignBudgetBelowMinToAdCenterMinBudget: helpIdForDefaultCampaignBudgetBelowMinToAdCenterMinBudget = 'pop_BA_Import_IncreaseCampBudget',
      AdjustmentForBids_EnableAdjustment: helpIdForAdjustmentForBids_EnableAdjustment = 'Pop_BA_Import_AdjustBidValue',
      AdjustmentForCampaignBudgets_EnableAdjustment: helpIdForAdjustmentForCampaignBudgets_EnableAdjustment = 'Pop_BA_Import_AdjustBudgValue',
      ImportNewOption_ImportNewActiveEntitiesOnly: helpIdForImportNewOption_ImportNewActiveEntitiesOnly = 'pop_BA_Import_PauseCampaigns',
    } = importOptionFieldHelpIds;

    if (!this.shouldEnableBSCSmartImport) {
      this.addHelpButtonToSelector('[data-property="StoreIdForImportedProductAdExtensionsFlag"] span', 'Page_Import_ChangesToExistingCampaigns_BmcId', this.i18n.get('Import_ImportOptions_ShoppingCampaignOptions'));
    }
    this.addHelpButtonToSelector('[data-property="ImportNewOption_ImportNewActiveEntitiesOnly"] .checkbox label', helpIdForImportNewOption_ImportNewActiveEntitiesOnly, this.i18n.get('Import_ImportOptions_ImportNewPausedEntities'));
    this.addHelpButtonToSelector('[data-property="ImportDeletedEntities"] .checkbox label', 'pop_BA_Import_DeleteRemovedItems', this.i18n.get('ImportDeletedEntities'));
    if (ImportOptionsForm.isImportAutoMinimumBidEnabled(this.deps)) {
      this.addHelpButtonToSelector('[data-property="DefaultBidsBelowMinToAdCenterMinBid"] .checkbox label', helpIdForDefaultBidsBelowMinToAdCenterMinBid, this.i18n.get('Import_ImportOptions_SetMinBids_RecommendedBid'));
    } else {
      this.addHelpButtonToSelector('[data-property="DefaultBidsBelowMinToAdCenterMinBid"] .checkbox label', helpIdForDefaultBidsBelowMinToAdCenterMinBid, this.i18n.get('Import_ImportOptions_SetMinBids_Generic_WithRecommendedBid'));
    }
    this.addHelpButtonToSelector('[data-property="DefaultCampaignBudgetBelowMinToAdCenterMinBudget"] .checkbox label', helpIdForDefaultCampaignBudgetBelowMinToAdCenterMinBudget, this.i18n.get('Import_ImportOptions_SetMinBudget_Generic'));
    this.addHelpButtonToSelector('[data-property="ImportUpdateEntityPreference_UpdateNameForExistingCampaigns"] .checkbox label', 'pop_BA_Import_DoNotUpdateNames', this.i18n.get('ImportUpdateEntityPreference_UpdateNameForExistingCampaigns'));
    this.addHelpButtonToSelector('[data-property="ImportNewOption_ImportNewCampaignsAndMarkAsPaused"] .checkbox label', 'pop_BA_Import_PauseImportedCamp', this.i18n.get('ImportNewOption_ImportNewCampaignsAndMarkAsPaused'));
    this.addHelpButtonToSelector('[data-property="ImportNewOption_ImportAllAdUnderAnAdGroup"] .checkbox label', 'pop_BA_Import_IncludePausedAds', this.i18n.get('Import_ImportOptions_ImportNewPausedAds'));
    this.addHelpButtonToSelector('[data-property="RaiseLowBidsToMinServingBidsForBsc"] .checkbox label', 'pop_BA_Import_ProductGroupBids', this.i18n.get('Import_ImportOptions_SetMinShoppingCampaignBid'));
    this.addHelpButtonToSelector('[data-property="AutoDeviceBidOptimization"] .checkbox label', 'Pop_BA_ImportRF_PcNegativeBidModifierDoNotImport', this.i18n.get('Import_ImportOptions_AutoDeviceBidOptimization'));
    this.addHelpButtonToSelector('[data-property="PauseParentIfNoLocationSupported"] .checkbox label', 'Pop_BA_Import_PauseCampaignsUnsupportedLT', this.i18n.get('Import_ImportOptions_PauseParentIfNoLocationSupported'));
    this.addHelpButtonToSelector('[data-property="EnableParentLocationMapping"] .checkbox label', 'Pop_BA_Import_DoNoteExpandUnsupportedLT', this.i18n.get('Import_ImportOptions_EnableParentLocationMapping'));
    this.addHelpButtonToSelector('[data-property="ImportUpdateEntityPreference_UpdateCampaignBudget"] .checkbox label', 'Pop_BA_Import_UpdateExAGCampBudg', this.i18n.get('ImportUpdateEntityPreference_UpdateCampaignBudget_BidSection'));
    this.addHelpButtonToSelector('[data-property="ImportUpdateEntityPreference_UpdateAdGroupAndKeywordBids"] .checkbox label', 'Pop_BA_Import_UpdateBids', this.i18n.get('ImportUpdateEntityPreference_UpdateAdGroupAndKeywordBids_Bids'));
    this.addHelpButtonToSelector('[data-property="ImportUpdateEntityPreference_UpdateAdGroupAndKeywordBidStrategies"] .checkbox label', 'Pop_BA_Import_UpdateBidStrat', this.i18n.get('ImportUpdateEntityPreference_UpdateAdGroupAndKeywordBids_BidStrategies'));
    this.addHelpButtonToSelector('[data-property="PauseAIMAdGroupIfAllAudienceCriterionNotImported"] .checkbox label', 'Pop_BA_Import_PauseAdGroupsUnsupported', this.i18n.get('Import_ImportOptions_PauseAIMAdGroupIfAllAudienceCriterionNotImported'));

    this.addHelpButtonToSelector('[data-property="AdjustmentForBids_EnableAdjustment"] .checkbox label', helpIdForAdjustmentForBids_EnableAdjustment, this.i18n.get('AdjustmentForBids_EnableAdjustment'));
    this.addHelpButtonToSelector('[data-property="AdjustmentForCampaignBudgets_EnableAdjustment"] .checkbox label', helpIdForAdjustmentForCampaignBudgets_EnableAdjustment, this.i18n.get('AdjustmentForCampaignBudgets_EnableAdjustment'));
    this.addHelpButtonToSelector('[data-property="ImportNewCustomFiltersForShoppingCampaigns"] .checkbox label', 'pop_BA_Import_NewProductFilters', this.i18n.get('Import_ImportOptions_ImportNewCustomFiltersForShoppingCampaigns'));
    this.addHelpButtonToSelector('[data-property="UpdateCustomFiltersForShoppingCampaigns"] .checkbox label', 'pop_BA_Import_UpdateProductFilters', this.i18n.get('Import_ImportOptions_UpdateCustomFiltersForShoppingCampaigns'));
    this.addHelpButtonToSelector('[data-property="ImportSearchAndDsaMixedCampaignAsSearchCampaign"] .checkbox label', 'pop_BA_Import_MixedMode', this.i18n.get('Import_ImportOptions_ImportDSAMixedAsSearch'));
    this.addHelpButtonToSelector('[data-property="ImportUET"] .control-label', 'pop_BA_Import_UETTag', this.i18n.get('Import_ImportOptions_UETTag_Label'));
    this.addHelpButtonToSelector('[data-property="ImportNewOption_ImportNewLogoAdExtensions"] .checkbox label', ': pop_BA_Import_LogoExt', this.i18n.get('ImportNewOption_ImportNewLogoAdExtensions'));
    this.addHelpButtonToSelector('[data-property="ImportUpdateEntityPreference_UpdateLogoAdExtensions"] .checkbox label', ': pop_BA_Import_LogoExt', this.i18n.get('ImportUpdateEntityPreference_UpdateLogoAdExtensions'));
    this.addHelpButtonToSelector('[data-property="ImportNewOption_ImportNewConversionGoals"] .checkbox label', ': pop_BA_Import_Google_ConvGoalsNew', this.i18n.get('ImportNewOption_ImportNewConversionGoals'));
    this.addHelpButtonToSelector('[data-property="ImportUpdateEntityPreference_UpdateConversionGoals"] .checkbox label', ': pop_BA_Import_Google_ConvGoalsExist', this.i18n.get('ImportUpdateEntityPreference_UpdateConversionGoals'));
  }

  addHelpButtonToSelector(selector, helpId, ariaLabelContext) {
    const learnMoreLinkText = this.i18n.get('LearnMoreLinkText');

    if (this.$(selector).length) {
      this.$(selector).append(helpButtonTemplate({
        helpId,
        ariaLabelContext,
        arialLabelText: learnMoreLinkText,
      }));
    }
  }

  remove() {
    this.errorMessageView.remove();
    this.uetErrorMessageView.remove();
    super.remove();
  }

  // TODO: remove
  static generateImportUserPreferencesFromPreviousOptions(deps, previousOptions) {
    const model = parseExistingImportOptions(deps, previousOptions);
    const importUserPreferences = parseModelToImportUserPreference(
      model,
      ImportOptionsForm.isPageFeedEnabled(deps),
      false,
      ImportOptionsForm.isDeviceTargetOptimizationEnabled(deps),
      ImportOptionsForm.isAdScheduleUseSearcherTimezoneEnabled(deps),
      ImportOptionsForm
        .isPauseAIMAdGroupIfAllAudienceCriterionNotImportedImportOptionEnabled(deps),
      ImportOptionsForm
        .isGoogleImageExtensionImportOptionEnabled(deps)
    );
    return importUserPreferences;
  }


  static isDeviceTargetOptimizationEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};
    return permissions.IsDeviceTargetOptimizationEnabled;
  }

  static isAdScheduleUseSearcherTimezoneEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};
    return permissions.IsAdScheduleUseSearcherTimezoneEnabled;
  }

  static isPauseAIMAdGroupIfAllAudienceCriterionNotImportedImportOptionEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};
    return permissions.IsPauseAIMAdGroupIfAllAudienceCriterionNotImportedImportOptionEnabled;
  }

  static isGoogleImageExtensionImportOptionEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};
    return permissions.IsGoogleImageExtensionImportOptionEnabled;
  }

  static isGoogleLogoExtensionImportOptionEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};

    return permissions.dynamic && permissions.dynamic.GoogleLogoExtensionImportOptionEnabled;
  }

  static isGoogleUpdateAdUrlsImportOptionEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};

    return permissions.dynamic && permissions.dynamic.GoogleUpdateAdUrlsImportOptionEnabled;
  }

  static isGoogleUpdateSitelinkUrlsImportOptionEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};

    return permissions.dynamic && permissions.dynamic.GoogleUpdateSitelinkUrlsImportOptionEnabled;
  }

  static isGoogleAccountNegativeKeywordsImportOptionEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};

    return permissions && permissions.IsAccountNegativeKeywordsForGoogleImportOptionEnabled;
  }

  static isPageFeedEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};

    return permissions.IsPageFeedImportOptionEnabled;
  }

  static isBSCSmartImportEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};

    return permissions.IsBSCSmartImportEnabled;
  }

  static isAudienceTargetForFBImportOptionEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};

    return permissions.IsAudienceTargetForFBImportOptionEnabled;
  }

  static isGoogleAdCustomizerAttributesImportOptionEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};

    return permissions.dynamic && permissions.dynamic.RSAAdCustomizerEnhancement;
  }

  static isGoogleConversionGoalsImportOptionEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};

    return permissions.IsGoogleConversionGoalsImportEnabled;
  }

  static isImportAutoMinimumBidEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};

    return permissions.IsImportAutoMinimumBidEnabled;
  }

  static isBrandSuitabilityImportOptionEnabled(deps) {
    const permissions = deps.config.get('Permissions') || {};

    return permissions.isBrandSuitabilityImportOptionEnabled;
  }
}
